/**
 * OrgNewsFeed - Organization-level Facebook-style news feed
 * Users can post text + photos. Task/epic comments with includeInOrgFeed flag also appear here.
 */
import { useMemo } from 'react';
import { Building2, Globe } from 'lucide-react';
import { useApp } from '../providers/AppProvider';
import { NewsFeedComposer, NewsFeedPostCard } from '../components/NewsFeedShared';
import { useNewsFeed } from '../hooks/useNewsFeed';
import { WIDGET_MUTED_TEXT_CLASS, WIDGET_PANEL_CLASS } from '../constants/widgetTheme';

export default function OrgNewsFeed() {
  const { state } = useApp();

  const currentUser = state?.currentUser;
  const org = state?.currentOrganization;
  const statePosts = useMemo(
    () => (state?.newsFeedPosts ?? []).filter((post) => post.organizationId === org?.id && post.scope === 'org'),
    [org?.id, state?.newsFeedPosts]
  );
  const {
    posts,
    loading,
    createPost,
    editPost,
    deletePost,
    commentOnPost,
    reactToPost,
  } = useNewsFeed({
    organizationId: org?.id,
    currentUser,
    fallbackPosts: statePosts,
  });

  if (!currentUser || !org) {
    return (
      <div className={`flex h-64 items-center justify-center ${WIDGET_MUTED_TEXT_CLASS}`}>
        Loading news feed...
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          {org.logo ? (
            <img src={org.logo} alt={org.name} className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <Building2 className="h-5 w-5 text-primary" />
          )}
          <h1 className="text-2xl font-semibold text-foreground">News Feed</h1>
        </div>
        <p className={`ml-9 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{org.name} · {posts.length} post{posts.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="space-y-4">
        {/* Composer */}
        <div className="mb-6">
          <NewsFeedComposer
            currentUserName={currentUser.name}
            currentUserAvatar={currentUser.avatar}
            fallbackAvatarSrc={org.logo}
            placeholder="What's on your mind?"
            onSubmit={createPost}
          />
        </div>

        {/* Feed */}
        {loading ? (
          <div className={`py-12 text-center ${WIDGET_MUTED_TEXT_CLASS}`}>Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className={`${WIDGET_PANEL_CLASS} py-16 text-center`}>
            <Globe className="mx-auto mb-3 h-12 w-12 text-muted-foreground/60" />
            <p className="font-medium text-foreground">No posts yet</p>
            <p className={`mt-1 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>Be the first to share something with your organization!</p>
          </div>
        ) : (
          posts.map((post) => (
            <NewsFeedPostCard
              key={post.id}
              post={post}
              currentUserId={currentUser.id}
              currentUserName={currentUser.name}
              currentUserAvatar={currentUser.avatar}
              onEdit={editPost}
              onDelete={deletePost}
              onComment={commentOnPost}
              onReact={reactToPost}
            />
          ))
        )}
      </div>
    </div>
  );
}
